<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import useCompanyImageBaseUrl from '@Use/useCompanyImageBaseUrl';

import { FlowLayout } from '@Shared';
import AuthFormShell from '@Components/AuthFormShell.vue';

const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const companyImageBaseUrl = useCompanyImageBaseUrl();
const route = useRoute();

// Two distinct outcomes land here:
//   - reason=cooldown → a recoverable ~10-minute cooldown (resend cap / issuance limit):
//     "try again after 10 minutes" is accurate — there IS something to wait for.
//   - no reason (terminal Failed / Locked) → the session is done; "10 minutes" would be
//     misleading because waiting changes nothing — the user must start over.
const isCooldown = computed(() => route.query.reason === 'cooldown');
const failureMessage = computed(() =>
    isCooldown.value
        ? "We couldn't verify your identity. Please try again after 10 minutes."
        : "We couldn't verify your identity. Please start over.",
);
</script>

<template>
    <flow-layout
        suppress-back-to-dashboard
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl">

        <auth-form-shell heading="Verification limit reached">
            <template #subheading>
                <span data-testid="mfa-failed-message">
                    {{ failureMessage }}
                </span>
            </template>

            <p class="mfa-failed__support">
                If you have questions, contact
                <a href="mailto:support@certifid.com">support@certifid.com</a>.
            </p>
        </auth-form-shell>
    </flow-layout>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.mfa-failed {
    &__support {
        color: $black-high;
    }
}
</style>
